import INTOUCH_API from "../../services/Api";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useCustomNavigate from "../../hooks/useCustomNavigate";
import PersonalAccount from "../../store/PersonalAccount";
import Game from "../../store/Game";
import { CURRENT_GAME_KEY } from "../../utils/constants";
import Pricing from "../../store/Pricing";
import is from "is_js";
import i18n from "i18next";
import useCurrentGame from "../../hooks/useCurrentGame";
import { useLocation, useParams } from "react-router-dom";
import useWebViewInAppBuyClick from "../../hooks/useWebViewInAppBuyClick";
import ROUTES from "../../utils/routes.js";
import useModalActions from "../../hooks/useModalActions";
import { MODAL_TYPES } from "../../utils/emitter";
import Payment from "../../store/Payment";
import isPayplusDomain from "../../utils/isPayplusDomain";
import { isIOSWebView, isWebView } from "../../utils/helpers";
import { userFinishedBase } from "../../utils/extendedProgress";

// 30.10: PayPlus открывается не в рамке, а целиком (переход на их страницу). В рамке
// Apple Pay держался на мосте PayPlus, нашем файле домена и Safari 17+ и регулярно
// ломался; на их странице кошельки работают сами. Перед переходом запоминаем заказ,
// PayPlus возвращает на /payment-result/:status/:order, и эта же страница ждёт
// подтверждения. Заказы храним по номеру (последние 5, не дольше 6 часов): в истории
// браузера могут остаться страницы PayPlus от прежних попыток.
const PENDING_PAYMENTS_KEY = "pendingPayplusPayments";
const LEGACY_PENDING_KEY = "pendingPayplusPayment"; // формат первой выкладки 30.10
const PENDING_TTL_MS = 6 * 60 * 60 * 1000;
const PENDING_MAX = 5;

const readPendingPayments = () => {
  try {
    const all = JSON.parse(localStorage.getItem(PENDING_PAYMENTS_KEY)) || {};
    const now = Date.now();
    return Object.fromEntries(
      Object.entries(all).filter(([, p]) => p && now - p.ts < PENDING_TTL_MS)
    );
  } catch (e) {
    return {};
  }
};

const writePendingPayments = (all) => {
  try {
    localStorage.setItem(PENDING_PAYMENTS_KEY, JSON.stringify(all));
  } catch (e) {
    // хранилище недоступно (приватный режим старых Safari): без сохранённого
    // заказа возврат с успехом просто ведёт в ЛК
  }
};

const savePendingPayment = (payment) => {
  const all = readPendingPayments();
  all[payment.order] = { ...payment, ts: Date.now() };
  const latest = Object.entries(all)
    .sort(([, a], [, b]) => b.ts - a.ts)
    .slice(0, PENDING_MAX);
  writePendingPayments(Object.fromEntries(latest));
};

const removePendingPayment = (order) => {
  const all = readPendingPayments();
  delete all[order];
  writePendingPayments(all);
  localStorage.removeItem(LEGACY_PENDING_KEY);
};

// Заказ из адреса возврата; без номера в адресе (ссылка создана до 30.10 вечером)
// берём последний сохранённый.
const findPendingPayment = (order) => {
  const all = readPendingPayments();
  if (order) return all[order] || null;
  const [latest] = Object.values(all).sort((a, b) => b.ts - a.ts);
  if (latest) return latest;
  try {
    return JSON.parse(localStorage.getItem(LEGACY_PENDING_KEY)) || null;
  } catch (e) {
    return null;
  }
};

// Товары Этапа 2: GAME (легаси-вход без state), REPEAT_TEST, BLANK_FORM, EXTENDED_57.
// Провайдер выбирается автоматически по домену (ТЗ «Нью» #8): .ru → Prodamus ₽,
// остальные → PayPlus $; выбор руками убран. iOS WebView с покупкой игры — Apple Pay,
// прочие товары на iOS идут через веб-провайдера (IAP вне скоупа Этапа 2).
export default function useController() {
  const { navigateTo } = useCustomNavigate();
  const { getCurrentGame } = useCurrentGame();
  const { t } = useTranslation();
  const location = useLocation();
  // возврат со страницы PayPlus: success | failure | cancel и номер заказа (на /payment их нет)
  const { status: returnStatus, order: returnOrder } = useParams();
  const [pending] = useState(() =>
    returnStatus ? findPendingPayment(returnOrder) : null
  );
  // eslint-disable-next-line no-unused-vars
  const game = getCurrentGame();
  const [order] = useState(
    () => (pending && pending.order) || new Date().getTime()
  );
  const { openModal } = useModalActions();
  // 10.10: окно «Оплата прошла успешно». Поллинг идёт каждые 3 с, и два запроса
  // могут успеть подтвердить покупку, поэтому показываем один раз.
  const successShownRef = useRef(false);
  const showPaymentSuccess = () => {
    if (successShownRef.current) return;
    successShownRef.current = true;
    if (pending) removePendingPayment(pending.order);
    openModal(MODAL_TYPES.PAYMENT_SUCCESS);
  };
  // 30.10: подтверждение обрабатываем один раз. Интервал не ждёт ответов, и при
  // медленном сервере второй тик тоже получал успех и делал второй переход.
  const confirmedRef = useRef(false);
  const takeConfirmation = () => {
    if (confirmedRef.current) return false;
    confirmedRef.current = true;
    return true;
  };
  // ушли со страницы, пока создавалась ссылка: на PayPlus уже не переходим
  const unmountedRef = useRef(false);
  const [formLink, setFormLink] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  // вернулись с оплатой, а колбэк PayPlus всё не приходит: показываем, что ждать в ЛК
  const [isPaymentSlow, setIsPaymentSlow] = useState(false);
  // Резолвим gameId для +57 ОДИН раз и запоминаем: после успешной оплаты у игры
  // появляется hasExtendedSet=true, и повторный resolveExt57GameId() её бы отсеял
  // (фильтр !hasExtendedSet) → редирект в свайпер +57 ушёл бы не в ту игру / в ЛК.
  // После возврата с PayPlus берём уже подобранный до оплаты.
  const ext57GameIdRef = useRef((pending && pending.ext57GameId) || null);
  const { handleBuyInAppWebViewClick } = useWebViewInAppBuyClick(
    game ? game.id : null,
    location
  );

  // после возврата с PayPlus location.state нет: товар и откуда пришли берём из сохранённого
  const paymentState = (pending && pending.state) || location.state || {};
  const { productType = "GAME", gameId: stateGameId, from } = paymentState;
  // Легаси: покупка игры оформляется как апгрейд демо (type DEMO_GAME)
  const backendType = productType === "GAME" ? "DEMO_GAME" : productType;
  const isApplePayFlow =
    !returnStatus && isIOSWebView() && productType === "GAME";
  const provider = isApplePayFlow
    ? "ios"
    : isPayplusDomain()
    ? "payplus"
    : "prodamus";
  // 30.10: в приложении (WebView) PayPlus остаётся в рамке. Скрипт приложения подменяет
  // window.postMessage на главной странице, и на странице PayPlus это ломало бы её
  // собственные сообщения (окно 3-D Secure). Apple Pay в WebView всё равно нет.
  const payplusRedirect = !isWebView();

  // fix: +57 (EXTENDED_57) можно купить ТОЛЬКО для связанной игры (бэк отдаёт
  // CONNECT_REQUIRED без gameId у игры с партнёром). Из ЛК gameId приходит через
  // state, но с ЛЕНДИНГА его нет → раньше заказ падал 400, форма не грузилась
  // (пустая страница «Оплатить игру») и поллинг стучал 400 без остановки.
  // Теперь для +57 сами находим подходящую связанную игру покупателя:
  // участник пары (invitedUserId есть) + набор ещё не куплен (+57/PREMIUM нет);
  // при прочих равных — где базовый тест уже пройден.
  const resolveExt57GameId = () => {
    const uid = PersonalAccount.user?.id;
    const games = PersonalAccount.games || [];
    if (uid == null || !games.length) return null;
    const isParticipant = (g) =>
      g.invitedUserId &&
      (Number(g.userId) === Number(uid) ||
        Number(g.invitedUserId) === Number(uid));
    const eligible = games.filter(
      (g) => isParticipant(g) && !g.hasExtendedSet && g.type !== "PREMIUM"
    );
    if (!eligible.length) return null;
    const baseDone = eligible.find((g) => userFinishedBase(g, uid));
    return (baseDone || eligible[0]).id;
  };

  // Единый источник gameId для +57 (buildOrderBody / pendingExt57 / поллинг успеха),
  // чтобы покупка, флажок авто-перехода и редирект вели в ОДНУ игру. Значение
  // кешируется в ref при первом резолве (до оплаты), чтобы после покупки —
  // когда у игры уже hasExtendedSet — вернуть тот же gameId, а не пере-подбирать.
  const getExt57GameId = () => {
    if (ext57GameIdRef.current != null) return ext57GameIdRef.current;
    let gid = null;
    if (stateGameId) {
      gid = stateGameId;
    } else {
      // currentGame берём, только если это связанная игра БЕЗ уже купленного +57
      // (иначе не подсовываем повторную покупку) — иначе подбираем по списку игр
      const cur = getCurrentGame();
      gid =
        cur && cur.invitedUserId && !cur.hasExtendedSet
          ? cur.id
          : resolveExt57GameId();
    }
    if (gid != null) ext57GameIdRef.current = gid;
    return gid;
  };

  const buildOrderBody = () => {
    const currentGame = getCurrentGame();
    let gameIdPart = {};
    if (productType === "EXTENDED_57") {
      // +57 привязан к игре коннекта — берём явный/текущий/подобранный gameId
      const gid = getExt57GameId();
      if (gid) gameIdPart = { gameId: gid };
    } else if (productType === "BLANK_FORM") {
      // пустая анкета — к игре, если передана
      if (stateGameId || currentGame) {
        gameIdPart = { gameId: stateGameId || currentGame?.id };
      }
    } else if (stateGameId) {
      // GAME с явным gameId = апгрейд КОНКРЕТНОЙ игры (кнопка «Игра Wepurplay»
      // под игрой в ЛК). Прикрепляем gameId, чтобы денежный путь апгрейдил ту же
      // игру, что и оплата баллами (иначе создалась бы новая пустая игра).
      gameIdPart = { gameId: stateGameId };
    } else if (
      !(
        location.state &&
        (from === ROUTES.PERSONAL_ACCOUNT_PAGE || from === ROUTES.REGISTER_PAGE)
      ) &&
      currentGame
    ) {
      // прежняя логика GAME: из ЛК/регистрации без gameId (создастся новая игра)
      gameIdPart = { gameId: currentGame.id };
    }

    return {
      order,
      type: backendType,
      lang: i18n.language,
      email: PersonalAccount.user.email,
      ...gameIdPart,
    };
  };

  // fix: любой сбой создания заказа больше не оставляет пустую страницу — показываем
  // причину (для +57 без пары бэк присылает локализованный CONNECT_REQUIRED).
  const showOrderError = (error) => {
    const raw = error?.response?.data?.message;
    const msg =
      raw && typeof raw === "object" ? raw.message : typeof raw === "string" ? raw : null;
    setErrorMessage(msg || t("payment_page.error_generic"));
  };

  const startPayment = () => {
    if (!order) return;
    const body = buildOrderBody();
    if (provider === "prodamus") {
      INTOUCH_API.requestManager(
        "order_id",
        { ...body, payment_method: "AC" },
        (response) => {
          setFormLink(response.data.form_url);
        },
        showOrderError
      );
    } else {
      INTOUCH_API.requestManager(
        "payplus_order_id",
        // redirect: бэк вернёт ссылку с возвратом на /payment-result/:status/:order
        { ...body, redirect: payplusRedirect },
        (response) => {
          const paymentLink = response.data.data.payment_page_link;
          if (!paymentLink) {
            setErrorMessage(t("payment_page.error_generic"));
            return;
          }
          if (!payplusRedirect) {
            setFormLink(paymentLink);
            return;
          }
          // ссылка создаётся до ~15 с; если за это время ушли со страницы, не уводим
          if (unmountedRef.current) return;
          savePendingPayment({
            order,
            state: { productType, gameId: stateGameId, from },
            ext57GameId: ext57GameIdRef.current,
          });
          // replace: иначе «Назад» со страницы PayPlus вернул бы на /payment, она
          // создала бы новый заказ и снова увела на оплату, выйти было бы нельзя
          window.location.replace(paymentLink);
        },
        showOrderError
      );
    }
  };

  // «Попробовать снова» после неудачной или отменённой оплаты: новый заказ с тем же товаром
  const retryPayment = () => {
    if (pending) removePendingPayment(pending.order);
    navigateTo(ROUTES.PAYMENT_PAGE, { replace: true, state: paymentState });
  };

  // из ЛК покупка видна и без ожидания здесь, сохранённый заказ больше не нужен
  const goToPersonalAccount = () => {
    if (pending) removePendingPayment(pending.order);
    navigateTo(ROUTES.PERSONAL_ACCOUNT_PAGE, { replace: true });
  };

  // Поллинг успеха оплаты: игра/повторный тест — через появление инвайт-ссылки (как раньше);
  // BLANK_FORM — по факту покупки, возврат туда, откуда пришли; EXTENDED_57 — по факту
  // покупки ВЕДЁМ покупателя сразу в свайпер 57 карт (иначе был тихий редирект в ЛК без
  // входа к картам — покупатель не мог их заполнить; разблокировку получал только партнёр).
  const checkPaymentSuccess = async () => {
    if (!order) return;
    if (productType === "EXTENDED_57") {
      await INTOUCH_API.requestManager(
        "check_purchased",
        { payload: { order } },
        async () => {
          if (!takeConfirmation()) return;
          // поллинг успел раньше редиректа провайдера (Prodamus/успех на /payment):
          // ведём сами и снимаем флажок, чтобы ЛК не увёл в свайпер повторно
          localStorage.removeItem("pendingExt57GameId");
          await PersonalAccount.getPersonalAccountData();
          const gid = getExt57GameId();
          // переключаем «текущую» игру на игру покупки, чтобы ответы +57 ушли в неё
          const eventGame = PersonalAccount.games?.find((g) => g.id === gid);
          if (eventGame) {
            Game.setGame(eventGame);
            localStorage.setItem(CURRENT_GAME_KEY, JSON.stringify(eventGame));
          }
          showPaymentSuccess();
          if (gid) {
            // item 6: вход в +57 через страницу инструкции
            navigateTo(ROUTES.EXTENDED_57_INSTRUCTION_PAGE, {
              state: { gameId: gid },
            });
          } else {
            // Task 1: +57 куплен БЕЗ пары — бэк создал DEMO-игру с инвайт-ссылкой.
            // Ведём в ЛК: игра без партнёра рендерится как InvitePair (карточка
            // «пригласи партнёра» с рабочей ссылкой) → покупатель зовёт партнёра,
            // +57 у пары откроется, когда тот зайдёт. (PayPlus и так редиректит в ЛК.)
            navigateTo(from || ROUTES.PERSONAL_ACCOUNT_PAGE);
          }
        },
        (error) => {}
      );
    } else if (productType === "BLANK_FORM") {
      await INTOUCH_API.requestManager(
        "check_purchased",
        { payload: { order } },
        async () => {
          if (!takeConfirmation()) return;
          await PersonalAccount.getPersonalAccountData();
          showPaymentSuccess();
          navigateTo(from || ROUTES.PERSONAL_ACCOUNT_PAGE);
        },
        (error) => {}
      );
    } else {
      await INTOUCH_API.requestManager(
        "invite_link",
        { payload: { order } },
        async () => {
          if (!takeConfirmation()) return;
          await PersonalAccount.getPersonalAccountData();
          // 10.10: страница приглашения берёт номер заказа из стора, но его туда
          // никто не записывал, и поле ссылки после покупки игры было пустым
          Payment.setPaymentOrder(order);
          showPaymentSuccess();
          navigateTo(ROUTES.PAYMENT_INVITE_PAGE);
        },
        (error) => {}
      );
    }
  };

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    (async () => {
      Pricing.fetch();
      // Для +57 нужен список игр покупателя, чтобы привязать заказ к связанной игре
      // (с лендинга gameId не приходит). Догружаем, если ЛК ещё не открывали.
      if (productType === "EXTENDED_57" && !PersonalAccount.games?.length) {
        await PersonalAccount.getPersonalAccountData();
      }
      // Вернулись со страницы PayPlus: новый заказ не создаём. При успехе ждём
      // подтверждения поллингом ниже; если заказа в этом браузере нет (оплачивали
      // в другом или почистили данные), ведём в ЛК, как PayPlus вёл раньше.
      if (returnStatus) {
        // PayPlus дописывает к адресу возврата свои параметры (данные платежа);
        // убираем их, чтобы не уходили в аналитику
        if (window.location.search) {
          window.history.replaceState(
            window.history.state,
            "",
            window.location.pathname
          );
        }
        if (returnStatus === "success" && !pending) goToPersonalAccount();
        return;
      }
      // Флажок для авто-перехода в свайпер +57 ПОСЛЕ оплаты: PayPlus возвращает
      // покупателя на свой redirect_url (/personal-account) раньше, чем успевает
      // сработать поллинг ниже, поэтому ЛК читает этот флажок и уводит покупателя
      // прямо к 57 картам (см. PersonalAccountPage/controller). Ставим при старте
      // оплаты набора; чистится либо в поллинге ниже (Prodamus/успех на /payment),
      // либо в ЛК после перехода.
      if (productType === "EXTENDED_57") {
        const gid = getExt57GameId();
        if (gid) localStorage.setItem("pendingExt57GameId", String(gid));
      }
      // 10.10: PayPlus после неудачной/отменённой оплаты открывает нашу /payment
      // ВНУТРИ своей рамки (refURL_failure/cancel). Без этой проверки вложенная
      // страница создавала новый заказ (GAME, $49) и вторую рамку оплаты внутри
      // первой. Внутри рамки заказ не создаём, показываем «оплата не прошла».
      let insideFrame = false;
      try {
        insideFrame = window.self !== window.top;
      } catch (e) {
        insideFrame = true;
      }
      if (insideFrame) {
        setErrorMessage(t("payment_page.not_completed"));
        return;
      }
      if (provider !== "ios") {
        startPayment();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // fix: поллим успех ТОЛЬКО когда форма оплаты реально загрузилась. Если заказ
    // не создался (нет formLink) — не стучим в check-purchased/invite-link каждые
    // 3с (это и был флуд 400 в консоли на пустой странице). Исключение — iOS Apple
    // Pay: там веб-форма не грузится (провайдер ios), но нативная покупка ловится
    // тем же поллингом, поэтому его оставляем.
    // После возврата с PayPlus поллим заказ, сохранённый перед переходом. При
    // неудаче или отмене тоже недолго проверяем: если списание всё же прошло (колбэк
    // пришёл), ведём по пути успеха, а не предлагаем заплатить второй раз.
    const isWaitingReturn = returnStatus === "success" && pending;
    const isCheckingFailed =
      returnStatus && returnStatus !== "success" && pending;
    if (
      provider !== "ios" &&
      !formLink &&
      !isWaitingReturn &&
      !isCheckingFailed
    )
      return;
    const refreshLinkID = setInterval(
      async () => await checkPaymentSuccess(),
      3000
    );
    // колбэк PayPlus обычно приходит за секунды; если за минуту нет, подсказываем,
    // что покупка появится в ЛК, а поллинг продолжаем (не дольше 15 минут)
    const slowID = isWaitingReturn
      ? setTimeout(() => setIsPaymentSlow(true), 60000)
      : null;
    const stopID =
      isWaitingReturn || isCheckingFailed
        ? setTimeout(
            () => clearInterval(refreshLinkID),
            isWaitingReturn ? 15 * 60000 : 15000
          )
        : null;
    return () => {
      clearInterval(refreshLinkID);
      clearTimeout(slowID);
      clearTimeout(stopID);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formLink]);

  useEffect(() => {
    if (is.mobile()) {
      document.head.querySelector('meta[name="viewport"]').content =
        "width=device-width, initial-scale=1, user-scalable=0";
    }
    return () => {
      document.head.querySelector('meta[name="viewport"]').content =
        "width=device-width, initial-scale=1";
    };
  }, []);

  return {
    formLink,
    errorMessage,
    isApplePayFlow,
    handleBuyInAppWebViewClick,
    returnStatus,
    canRetry: !!pending,
    isPaymentSlow,
    retryPayment,
    goToPersonalAccount,
  };
}
